import React, { useMemo, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col } from "reactstrap";
import * as moment from "moment";

import { ActivityReportData, AgencyRow } from "./types";

/**
 * An agency with no activity for this long is a risk signal, not just a
 * quiet row: it is relying on CIMS without using it.
 */
const INACTIVE_AFTER_DAYS = 90;

/** How many agencies show before the rest are folded away. */
const INITIAL_ROWS = 8;

type Status = "inactive" | "quiet" | "active" | "unstaffed";

const statusOf = (row: AgencyRow): Status => {
  // An agency nobody has an account for has not been onboarded yet; that is
  // not the same as an agency that stopped using CIMS, so it is never coral.
  if (row.members === 0 && row.visits === 0) return "unstaffed";
  if (
    !row.lastSeenAt ||
    moment().diff(moment(new Date(row.lastSeenAt)), "days") >=
      INACTIVE_AFTER_DAYS
  ) {
    return "inactive";
  }
  return row.visits > 0 ? "active" : "quiet";
};

const lastSeenTime = (row: AgencyRow) =>
  row.lastSeenAt ? new Date(row.lastSeenAt).getTime() : 0;

/** Agencies needing attention first: longest-inactive, then quiet, then busiest. */
const STATUS_ORDER: Record<Status, number> = {
  inactive: 0,
  quiet: 1,
  active: 2,
  unstaffed: 3,
};

const StatusTag = ({ row, status }: { row: AgencyRow; status: Status }) => {
  if (status === "inactive") {
    return (
      <span className="badge bg-danger-subtle text-ink-danger">
        <i className="ri-alarm-warning-line me-1" aria-hidden="true"></i>
        {row.lastSeenAt
          ? `Inactive ${INACTIVE_AFTER_DAYS}+ days`
          : "No activity on record"}
      </span>
    );
  }
  if (status === "unstaffed") {
    return (
      <span className="badge bg-light text-ink-muted">No registered users</span>
    );
  }
  if (status === "quiet") {
    return (
      <span className="badge bg-light text-ink-muted">
        No activity this period
      </span>
    );
  }
  return (
    <span className="badge bg-success-subtle text-ink-success">Active</span>
  );
};

/**
 * The lead of the report: every agency in scope, with the ones that have
 * stopped using CIMS at the top, so the oversight question "who isn't using
 * it?" is answered before anything else.
 */
const AgencyEngagement = ({
  data,
  loading,
}: {
  data: ActivityReportData | null;
  loading: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(
    () =>
      (data?.agencies || [])
        .map((row) => ({ row, status: statusOf(row) }))
        .sort(
          (a, b) =>
            STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
            (a.status === "active"
              ? b.row.visits - a.row.visits
              : lastSeenTime(a.row) - lastSeenTime(b.row)) ||
            a.row.agencyName.localeCompare(b.row.agencyName)
        ),
    [data]
  );

  const activeCount = rows.filter((r) => r.status === "active").length;
  const inactiveCount = rows.filter((r) => r.status === "inactive").length;
  const unstaffedCount = rows.filter((r) => r.status === "unstaffed").length;
  const staffedCount = rows.length - unstaffedCount;
  const visible = expanded ? rows : rows.slice(0, INITIAL_ROWS);

  return (
    <Col xs={12}>
      <Card className="agency-engagement">
        <CardHeader className="d-flex align-items-center gap-3 flex-wrap">
          <div className="flex-grow-1">
            <h4 className="card-title mb-1">Agency engagement</h4>
            <p className="text-ink-muted fs-13 mb-0">
              Every agency in scope, counted by its members&apos; activity.
              Agencies that need attention are listed first.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="placeholder-glow" aria-busy="true">
              <span className="placeholder col-4 mb-3 d-block"></span>
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className="placeholder col-12 mb-2 d-block"
                  style={{ height: 32 }}
                ></span>
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-ink-muted py-5">
              No agencies match the current filters.
            </div>
          ) : (
            <>
              <div className="agency-engagement__summary">
                <p className="agency-engagement__headline mb-0">
                  <strong>{activeCount}</strong> of{" "}
                  <strong>{staffedCount}</strong>{" "}
                  {staffedCount === 1 ? "agency" : "agencies"} with users used
                  CIMS in this period
                </p>
                {inactiveCount > 0 ? (
                  <span className="badge bg-danger-subtle text-ink-danger fs-12">
                    <i
                      className="ri-alarm-warning-line me-1"
                      aria-hidden="true"
                    ></i>
                    {inactiveCount} inactive for {INACTIVE_AFTER_DAYS}+ days
                  </span>
                ) : (
                  <span className="badge bg-success-subtle text-ink-success fs-12">
                    No agency inactive for {INACTIVE_AFTER_DAYS}+ days
                  </span>
                )}
                {unstaffedCount > 0 ? (
                  <span className="text-ink-muted fs-13">
                    {unstaffedCount}{" "}
                    {unstaffedCount === 1 ? "agency has" : "agencies have"} no
                    registered users yet
                  </span>
                ) : null}
              </div>

              <div className="table-responsive">
                <table className="table align-middle mb-0 agency-engagement__table">
                  <thead>
                    <tr>
                      <th scope="col">Agency</th>
                      <th scope="col">Status</th>
                      <th scope="col" className="text-end">
                        Visits
                      </th>
                      <th scope="col">Users engaged</th>
                      <th scope="col">Last active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(({ row, status }) => (
                      <tr key={row.agencyId}>
                        <td
                          data-label="Agency"
                          className="agency-engagement__name"
                        >
                          <span
                            className="fw-medium text-truncate d-block"
                            title={row.agencyName}
                          >
                            {row.agencyName}
                          </span>
                        </td>
                        <td data-label="Status">
                          <StatusTag row={row} status={status} />
                        </td>
                        <td
                          data-label="Visits"
                          className="text-end agency-engagement__num"
                        >
                          {row.visits.toLocaleString()}
                        </td>
                        <td data-label="Users engaged">
                          {row.members > 0 ? (
                            <div className="d-flex align-items-center gap-2">
                              <span className="agency-engagement__num text-nowrap fs-13">
                                {row.activeUsers} of {row.members}
                              </span>
                              <div
                                className="agency-engagement__bar"
                                role="img"
                                aria-label={`${row.engagedShare}% of members active`}
                              >
                                <span
                                  style={{
                                    width: `${Math.min(
                                      100,
                                      row.engagedShare
                                    )}%`,
                                  }}
                                ></span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-ink-muted fs-13">
                              No members
                            </span>
                          )}
                        </td>
                        <td
                          data-label="Last active"
                          className={`text-nowrap fs-13 ${
                            status === "inactive"
                              ? "text-ink-danger"
                              : "text-ink-muted"
                          }`}
                          title={
                            row.lastSeenAt
                              ? moment(new Date(row.lastSeenAt)).format(
                                  "DD MMM YYYY, HH:mm"
                                )
                              : undefined
                          }
                        >
                          {row.lastSeenAt
                            ? moment(new Date(row.lastSeenAt)).fromNow()
                            : "Never"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {rows.length > INITIAL_ROWS ? (
                <Button
                  color="link"
                  className="agency-engagement__more"
                  onClick={() => setExpanded((value) => !value)}
                  aria-expanded={expanded}
                >
                  {expanded
                    ? "Show fewer agencies"
                    : `Show all ${rows.length} agencies`}
                  <i
                    className={`ri-arrow-${
                      expanded ? "up" : "down"
                    }-s-line ms-1`}
                    aria-hidden="true"
                  ></i>
                </Button>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default AgencyEngagement;
